import m, {type Children, MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'

import {ScrollReplayCount} from './scroll_replay_count'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const statMeta = {
    tier: 'content',
    summary: 'Headline statistic — a count-up number or static text (e.g. `"24/7"`) — in a frosted-glass panel.',
    when_to_use:
        'Designed to sit over a photo: a `Card` `overlay`, or several side by side in a `HeroBand` `aside` (use `variant="light"` there). A numeric `value` counts up on scroll into view; a string `value` renders as-is. Wrap in `AsyncData` to drive `value` from an API. Owns its own glass look.',
    example: `
    <Stat label="Offers in stock" suffix="+" value={3500} />
    `,
} satisfies CmsComponentMeta

export interface StatAttrs {
    /** The value to show. A number counts up on scroll into view; a string (e.g. `"24/7"`) is shown as-is. */
    value: number | string
    /** Appended after the value, e.g. `"+"` → `3,500+`. */
    suffix?: string
    /** Caption under the value. */
    label: Children
    /** Glass look: `dark` (default) sits over any photo; `light` is a lighter wash for a `HeroBand` `aside`. */
    variant?: 'dark' | 'light'
}

/**
 * A single headline statistic in a frosted-glass panel. A numeric `value` counts up on scroll into view;
 * a string `value` (e.g. `"24/7"`) renders statically. Designed to sit over a photo (e.g. a `Card`
 * `overlay` or a `HeroBand` `aside`); owns its own glass appearance.
 */
export class Stat extends MithrilComponent<StatAttrs> {
    view(vnode: m.Vnode<StatAttrs>) {
        const {value, suffix, label, variant} = vnode.attrs
        return (
            <div className={classes('cms-stat', {'cms-stat--light': variant === 'light'})}>
                {typeof value === 'number' && Number.isFinite(value) ? (
                    <ScrollReplayCount
                        className='cms-stat-value scroll-replay-count'
                        start={value > 0 ? 1 : 0}
                        suffix={suffix}
                        target={value}
                    />
                ) : (
                    <span className='cms-stat-value'>
                        {value}
                        {suffix ?? ''}
                    </span>
                )}
                <span className='cms-stat-label'>{label}</span>
            </div>
        )
    }
}
